import React from 'react'
import { DateFns, YearMonthFns } from '../utils/datetime'

export interface useDatePickerResult {
  transitionState: 'none' | 'toNext' | 'toPrev' | 'init'
  onTransitionComplete(): unknown

  goToNextMonth(): unknown
  goToPreviousMonth(): unknown

  yearMonth: string
  exitingYearMonth: string

  focus: string | null
  setFocus(date: string | null): void

  value: string | null
  onChange(date: string): unknown

  shouldDisableDate(date: string): boolean
}

export interface useDatePickerProps {
  value: string | null
  onChange(date: string): unknown
  shouldDisableDate?(date: string): boolean
}

export function useDatePicker({ value: date, onChange, shouldDisableDate }: useDatePickerProps): useDatePickerResult {
  const [{ month, transition, previousMonth, internalFocus }, setState] = React.useState({
    month: YearMonthFns.toYearMonth(date ? new Date(`${date}T00:00`) : new Date()),
    previousMonth: '',
    transition: 'init' as 'none' | 'toNext' | 'toPrev' | 'init',
    internalFocus: (date ? date : DateFns.toISODate(new Date())) as string | null,
    nextFocus: null as string | null
  })

  const setFocus = React.useCallback(
    (focus: string) => {
      setState((s) => {
        const isNext = s.internalFocus && focus > s.internalFocus && focus.slice(0, 7) !== s.month
        const isPrev = s.internalFocus && focus < s.internalFocus && focus.slice(0, 7) !== s.month
        if (isNext) {
          return {
            previousMonth: s.month,
            month: YearMonthFns.getNext(s.month),
            transition: 'toNext',
            internalFocus: s.internalFocus,
            nextFocus: focus
          }
        } else if (isPrev) {
          return {
            previousMonth: s.month,
            month: YearMonthFns.getPrev(s.month),
            transition: 'toPrev',
            internalFocus: s.internalFocus,
            nextFocus: focus
          }
        } else {
          return {
            previousMonth: s.month,
            month: s.month,
            transition: 'none',
            internalFocus: focus,
            nextFocus: null
          }
        }
      })
    },
    [setState]
  )

  const goToNext = React.useCallback(() => {
    setState((s) => ({
      previousMonth: s.month,
      month: YearMonthFns.getNext(s.month),
      transition: 'toNext',
      internalFocus: null,
      nextFocus: null
    }))
  }, [setState])
  const goToPrev = React.useCallback(() => {
    setState((s) => ({
      previousMonth: s.month,
      month: YearMonthFns.getPrev(s.month),
      transition: 'toPrev',
      internalFocus: null,
      nextFocus: null
    }))
  }, [setState])

  const onTransitionComplete = React.useCallback(() => {
    setState((s) => ({
      previousMonth: s.month,
      month: s.month,
      transition: 'none',
      internalFocus: s.nextFocus || s.internalFocus,
      nextFocus: null
    }))
  }, [setState])

  return {
    yearMonth: month,
    exitingYearMonth: previousMonth,
    goToNextMonth: goToNext,
    goToPreviousMonth: goToPrev,
    transitionState: transition,
    onTransitionComplete: onTransitionComplete,
    setFocus,
    onChange,
    focus: internalFocus,
    value: date,
    shouldDisableDate: shouldDisableDate || (() => false)
  }
}
